import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import Box from '@mui/system/Box';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Tab from '@mui/material/Tab';
import { useAppDispatch } from '../../redux/hooks';
import { fetchOrdersByMadkholOrderId } from '../../redux/modules/jadwa/jadwa.actions';
// eslint-disable-next-line import/no-unresolved
import { jadwaSelector } from '../../redux/modules/jadwa/jadwa.selectors';
import { JadwaDetails } from './components/jadwa';
import { JadwaFundInvestAmounts } from './components/jadwa/JadwaFundInvestAmounts';
import { JadwaOrderTransfersSummary } from './components/jadwa/JadwaOrderTransfersSummary';
import { filterJadwaManagerOrders, getJadwaOrderTransfers, JADWA_FUND_MANAGER } from './components/jadwa/helpers';

interface JadwaTabProps {
  orderId: string;
  /** Which madkhol-jadwa manager's rows to show — `JADWA` (default), `ALBILAD` or `SNB`. */
  fundManager?: string;
}

export function JadwaTab({ orderId, fundManager = JADWA_FUND_MANAGER }: JadwaTabProps) {
  const dispatch = useAppDispatch();
  const { orders: managerOrders, error } = useSelector(jadwaSelector);
  // The store has every manager's rows (all live in madkhol-jadwa). Filtered once for the whole tab, so the summary
  // tables' row index still points at the right fund-order tab below them.
  const orders = useMemo(() => filterJadwaManagerOrders(managerOrders, fundManager), [managerOrders, fundManager]);
  const [selectedTab, setSelectedTab] = React.useState<string>('0');

  const fetchOrders = useCallback(() => {
    dispatch(fetchOrdersByMadkholOrderId(orderId));
  }, [orderId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (orderId) {
      fetchOrders();
    }
  }, [orderId]);

  const renderOrders = () => {
    if (!orders.length) {
      return;
    }

    // Every wire of the order once, so each fund order's Transfers tabs show all of them (USD and SAR).
    const orderTransfers = getJadwaOrderTransfers(orders).map(({ transfer }) => transfer);

    return (
      <TabContext value={selectedTab}>
        <JadwaFundInvestAmounts orders={orders} onSelectFundOrder={(index) => setSelectedTab(index.toString())} />
        <JadwaOrderTransfersSummary orders={orders} onSelectFundOrder={(index) => setSelectedTab(index.toString())} />
        <Box sx={{ width: '100%' }}>
          <TabList
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="jadwa fund orders"
          >
            {orders.map((order, index) => (
              <Tab
                key={`jadwa-fund-order-${order.id}`}
                value={index.toString()}
                label={`Fund Order #${order.fundOrderId}`}
                sx={{ textTransform: 'capitalize' }}
                disableRipple
              />
            ))}
          </TabList>
        </Box>
        {orders.map((order, index) => (
          <TabPanel key={`jadwa-fund-order-panel-${order.id}`} value={index.toString()} sx={{ width: '100%' }}>
            <JadwaDetails order={order} orderTransfers={orderTransfers} refresh={fetchOrders} />
          </TabPanel>
        ))}
      </TabContext>
    );
  };

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      {renderOrders()}
    </>
  );
}
